import html2pdf from 'html2pdf.js';
import QRCode from 'qrcode';

// Capacitor plugins — lazy-imported so the web build doesn't fail if the
// module resolves them at bundle time. Only touched inside the isNative
// branch, so the web bundle never actually reaches the require() call.
const isCapacitor = () => {
  try {
    const { Capacitor } = require('@capacitor/core');
    return Capacitor.isNativePlatform && Capacitor.isNativePlatform();
  } catch { return false; }
};

// v1.9.8 — full CR redesign inspired by Zambia Treasury Receipt style.
// Single A4 page, indigo brand palette, numbered field grid, item table,
// 3-column signatory footer with faded church stamp, brand header + footer
// bands, and a real QR code linking to the public /verify/<token> page.
//
// cr object fields (any may be empty):
//   cr_number, verify_token, date, received_from, spiritual_father_name,
//   hagest_sbket, city, fund_name, payment_method, amount_usd, amount_kwacha,
//   amounts (object), notes, member_card, lenco_reference, period_covered
//
// profile object fields:
//   church_name, church_address, church_phone, church_email, currencies,
//   signatory_<slot>_{name,title,signature}

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));

const abs = (relOrAbs) => {
  if (!relOrAbs) return '';
  if (/^(https?:|data:)/i.test(relOrAbs)) return relOrAbs;
  const origin = (typeof window !== 'undefined' && window.location && window.location.origin) || '';
  return origin + relOrAbs;
};
const withToken = (url) => {
  if (!url) return '';
  try {
    const tok = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (!tok) return url;
    return url + (url.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(tok);
  } catch { return url; }
};
const STAMP_URL = () => abs('/church-stamp.png');
const LOGO_URL  = () => abs('/logo.png');
const sigUrl = (path) => (path ? withToken(abs(path)) : '');

const formatAmount = (n, prefix) => {
  const v = parseFloat(n || 0);
  return v > 0 ? `${prefix}${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—';
};

// Synchronous QR data URI. qrcode's toDataURL is async; we call the sync
// generator behind it to keep buildReceipt synchronous. Returns '' if the
// token is missing so no image is rendered.
const buildQrDataUri = (verifyToken) => {
  if (!verifyToken) return '';
  const url = (typeof window !== 'undefined' && window.location ? window.location.origin : '') + `/verify/${verifyToken}`;
  try {
    // qrcode's toString('svg', ...) is synchronous.
    const svg = QRCode.toString(url, { type: 'svg', errorCorrectionLevel: 'M', margin: 0, width: 96 });
    if (typeof svg === 'string') return 'data:image/svg+xml;base64,' + btoa(svg);
  } catch (_) {}
  return '';
};
// qrcode.toString actually returns a Promise in some versions — provide a
// robust wrapper that produces an SVG data URI either way.
const qrSvgSync = (url) => {
  try {
    // eslint-disable-next-line new-cap
    const G = new QRCode.create ? null : null; // no-op to appease bundlers
  } catch (_) {}
  try {
    const modules = QRCode.create(url, { errorCorrectionLevel: 'M' }).modules;
    const size = modules.size;
    const scale = 4;
    const svgSize = size * scale;
    let rects = '';
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        if (modules.get(x, y)) {
          rects += `<rect x="${x*scale}" y="${y*scale}" width="${scale}" height="${scale}" fill="#111"/>`;
        }
      }
    }
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${svgSize} ${svgSize}" width="${svgSize}" height="${svgSize}" shape-rendering="crispEdges">${rects}</svg>`;
    return 'data:image/svg+xml;base64,' + btoa(svg);
  } catch (_) { return ''; }
};
const qrForCr = (verifyToken) => {
  if (!verifyToken) return '';
  const origin = (typeof window !== 'undefined' && window.location ? window.location.origin : '');
  return qrSvgSync(`${origin}/verify/${verifyToken}`);
};

// Build the 3-column signatory footer with stamp overlay. Reused by CR + PV.
const buildSignoff = (profile, { receivedLabel = 'RECEIVED BY' } = {}) => {
  const p = profile || {};
  const cell = (slot, label) => {
    const name = p[`signatory_${slot}_name`] || '';
    const title = p[`signatory_${slot}_title`] || '';
    const sig = p[`signatory_${slot}_signature`];
    return `
      <div class="sig-cell">
        <div class="sig-img">${sig ? `<img src="${esc(sigUrl(sig))}" alt="" />` : ''}</div>
        <div class="sig-line"></div>
        <div class="sig-name">${esc(name || '—')}</div>
        <div class="sig-title">${esc(title || '')}</div>
        <div class="sig-role">${esc(label)}</div>
      </div>
    `;
  };
  return `
    <footer class="signoff-3col">
      <img class="stamp" src="${esc(STAMP_URL())}" alt="" />
      <div class="sig-row">
        ${cell('prepared', 'PREPARED BY')}
        ${cell('approved', 'APPROVED BY')}
        ${cell('received', receivedLabel)}
      </div>
    </footer>
  `;
};

export const buildReceiptBlock = (cr, profile, copyLabel) => {
  const contactBits = [profile.church_address, profile.church_phone, profile.church_email].filter(Boolean).map(esc);
  const currencies = (profile.currencies && profile.currencies.length) ? profile.currencies : [profile.currency_primary || '$', profile.currency_secondary || 'K'];
  const cur1 = currencies[0] || '$';
  const cur2 = currencies[1] || 'K';
  const amts = cr.amounts && typeof cr.amounts === 'object'
    ? cr.amounts
    : { [cur1]: cr.amount_usd, [cur2]: cr.amount_kwacha };
  // Total (assumes single currency in practice; if multiple, we render one
  // row per non-zero currency and sum inside each currency).
  const items = currencies
    .map(sym => ({ sym, amount: parseFloat(amts[sym] || 0) }))
    .filter(x => x.amount > 0);
  const qrData = qrForCr(cr.verify_token);
  const origin = (typeof window !== 'undefined' && window.location ? window.location.origin : '');
  const verifyUrl = cr.verify_token ? `${origin}/verify/${cr.verify_token}` : '';

  const paymentLabel = (cr.payment_method || '')
    .replace(/[-_]/g, ' ')
    .replace(/\blenco\b/gi, 'Lenco')
    .replace(/\bmobile money\b/gi, 'Mobile Money');

  return `
    <section class="a4-page">
      <header class="band-top">
        <div class="logo"><img src="${esc(LOGO_URL())}" alt="" /></div>
        <div class="titles">
          <h1>${esc(profile.church_name || 'My Church')}</h1>
          ${profile.hagest_sbket || profile.city ? `<div class="subtitle">${esc([profile.hagest_sbket, profile.city].filter(Boolean).join(' · '))}</div>` : ''}
          ${contactBits.length ? `<div class="contact">${contactBits.join(' · ')}</div>` : ''}
        </div>
        <div class="qr">
          ${qrData ? `<img src="${esc(qrData)}" alt="QR" />` : `<div class="qr-placeholder"></div>`}
          <div class="qr-lbl">SCAN TO VERIFY</div>
        </div>
      </header>

      <span class="copy-badge">${esc(copyLabel)}</span>

      <div class="body">
        <h2 class="rct-title">Cash Receipt</h2>
        <div class="fields">
          <div class="field"><span class="n">1.</span><span class="k">Receipt Number:</span><span class="v acc">${esc(cr.cr_number || '—')}</span></div>
          <div class="field"><span class="n">2.</span><span class="k">Receipt Date:</span><span class="v strong">${esc(cr.date || '—')}</span></div>
          <div class="field"><span class="n">3.</span><span class="k">Received From:</span><span class="v strong">${esc(cr.received_from || '—')}</span></div>
          <div class="field"><span class="n">4.</span><span class="k">Member Card:</span><span class="v">${esc(cr.member_card || cr.card_number || '—')}</span></div>
          <div class="field"><span class="n">5.</span><span class="k">Spiritual Father:</span><span class="v">${esc(cr.spiritual_father_name || '—')}</span></div>
          <div class="field"><span class="n">6.</span><span class="k">Diocese:</span><span class="v">${esc(cr.hagest_sbket || '—')}</span></div>
          <div class="field"><span class="n">7.</span><span class="k">City:</span><span class="v">${esc(cr.city || '—')}</span></div>
          <div class="field"><span class="n">8.</span><span class="k">Payment Method:</span><span class="v cap">${esc(paymentLabel || '—')}</span></div>
          <div class="field"><span class="n">9.</span><span class="k">Reference:</span><span class="v mono">${esc(cr.lenco_reference || cr.reference || '—')}</span></div>
          <div class="field"><span class="n">10.</span><span class="k">Status:</span><span class="v" style="color:#166534;font-weight:700">${cr.deleted_at ? 'REVERSED' : 'PAID'}</span></div>
        </div>

        <div class="items">
          <table>
            <thead>
              <tr><th class="idx">#</th><th>Fund / Contribution</th><th class="num">Amount</th><th class="ccy">Currency</th></tr>
            </thead>
            <tbody>
              ${items.length
                ? items.map((it, i) => `<tr><td>${i + 1}</td><td>${esc(cr.fund_name || '—')}</td><td class="num">${it.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td><td class="num">${esc(it.sym)}</td></tr>`).join('')
                : `<tr><td>1</td><td>${esc(cr.fund_name || '—')}</td><td class="num">—</td><td class="num">—</td></tr>`}
              ${items.map((it, i) => `<tr class="total"><td colspan="2" style="text-align:right">${i === 0 ? 'TOTAL' : ''}</td><td class="num">${it.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td><td class="num">${esc(it.sym)}</td></tr>`).join('')}
            </tbody>
          </table>
        </div>

        ${cr.notes ? `<div class="notes"><span class="lbl">Notes:</span> ${esc(cr.notes)}</div>` : ''}

        ${buildSignoff(profile)}
        <div class="thanks">Thank you for your contribution.</div>
      </div>

      <footer class="band-bottom">
        <span class="verify">${verifyUrl ? `Verify: <span class="mono">${esc(verifyUrl)}</span>` : ''}</span>
        <span class="barcode">*${esc((cr.cr_number || '').replace(/[^A-Z0-9]/gi, ''))}*</span>
        <span class="page-x">1 of 1</span>
      </footer>
    </section>
  `;
};

export const RECEIPT_STYLES = `
  @page { size: A4; margin: 8mm; }
  /* v1.9.18 — force Chrome to print the indigo header/footer bands even
     when the user hasn't ticked "Background graphics". Without these
     properties, all .band-top/.band-bottom colours strip out on Save-as-PDF. */
  * { box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  body {
    margin: 0; padding: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans Ethiopic', Arial, sans-serif;
    color: #111827;
    background: #fff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .a4-page {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    page-break-inside: avoid;
    position: relative;
    overflow: hidden;
  }
  /* Header band */
  .band-top {
    background: #eef2ff; border-bottom: 1px solid #c7d2fe;
    display: grid; grid-template-columns: 90px 1fr 100px;
    align-items: center; padding: 10px 16px; gap: 14px;
  }
  .band-top .logo img { height: 74px; width: 74px; object-fit: contain; }
  .band-top .titles { text-align: center; }
  .band-top .titles h1 { font-family: 'Times New Roman', serif; font-size: 22px; margin: 0; font-weight: 700; color: #1e1b4b; letter-spacing: 0.02em; text-transform: uppercase; }
  .band-top .titles .subtitle { font-size: 12px; color: #374151; font-weight: 600; margin-top: 2px; }
  .band-top .titles .contact { font-size: 10.5px; color: #4b5563; margin-top: 3px; }
  .band-top .qr { text-align: center; }
  .band-top .qr img { width: 80px; height: 80px; border: 1px solid #c7d2fe; padding: 2px; background: #fff; }
  .band-top .qr .qr-placeholder { width: 80px; height: 80px; background: #f3f4f6; border: 1px dashed #c7d2fe; }
  .band-top .qr .qr-lbl { font-size: 8.5px; color: #374151; margin-top: 3px; letter-spacing: 0.06em; }
  .copy-badge {
    position: absolute; right: 22px; top: 108px;
    padding: 3px 10px; border: 1.5px solid #4f46e5; color: #4f46e5;
    font-size: 10px; letter-spacing: 0.14em; border-radius: 4px; font-weight: 800;
    background: rgba(255,255,255,.75);
  }
  .body { padding: 14px 20px 8px; position: relative; }
  .rct-title { font-family: 'Times New Roman', serif; font-size: 20px; font-weight: 700; color: #1e1b4b; margin: 0 0 10px; }
  .fields { display: grid; grid-template-columns: 1fr 1fr; gap: 3px 24px; }
  .field { display: grid; grid-template-columns: 24px 1fr 1fr; align-items: baseline; column-gap: 6px; font-size: 11.5px; padding: 3px 0; border-bottom: 1px dotted #d1d5db; }
  .field .n { color: #374151; font-weight: 700; }
  .field .k { color: #111827; font-weight: 700; }
  .field .v { color: #111827; text-align: right; }
  .field .v.strong { font-weight: 700; }
  .field .v.acc { color: #1e1b4b; font-weight: 800; }
  .field .v.mono { font-family: 'Courier New', monospace; font-size: 10.5px; }
  .field .v.cap { text-transform: capitalize; }
  .items { margin-top: 10px; }
  .items table { width: 100%; border-collapse: collapse; font-size: 12px; }
  .items thead th { background: #eef2ff; color: #1e1b4b; text-align: left; padding: 6px 8px; border-top: 1px solid #c7d2fe; border-bottom: 1px solid #c7d2fe; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.03em; }
  .items thead th.num { text-align: right; }
  .items thead th.ccy { text-align: right; width: 70px; }
  .items thead th.idx { text-align: center; width: 30px; }
  .items tbody td { padding: 6px 8px; border-bottom: 1px solid #eef2ff; }
  .items tbody td:first-child { text-align: center; }
  .items tbody td.num { text-align: right; }
  .items tbody tr.total td { background: #c7d2fe; color: #1e1b4b; font-weight: 800; padding-top: 7px; padding-bottom: 7px; border-bottom: none; }
  .notes { font-size: 10.5px; color: #374151; padding: 4px 0; font-style: italic; }
  .notes .lbl { color: #6b7280; }
  .thanks { font-style: italic; color: #6b7280; text-align: center; font-size: 10px; margin-top: 4px; }
  /* Signatory 3-col with stamp */
  .signoff-3col { position: relative; margin-top: 12px; padding-top: 8px; border-top: 1px solid #e5e7eb; min-height: 88px; }
  .signoff-3col .stamp { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -45%); height: 88px; opacity: 0.55; pointer-events: none; z-index: 0; }
  .signoff-3col .sig-row { position: relative; z-index: 1; display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
  .sig-cell { text-align: center; }
  .sig-cell .sig-img { height: 28px; display: flex; align-items: flex-end; justify-content: center; }
  .sig-cell .sig-img img { max-height: 28px; max-width: 100%; object-fit: contain; }
  .sig-cell .sig-line { border-top: 1px solid #111827; margin: 3px 12px 4px; }
  .sig-cell .sig-name { font-size: 11px; font-weight: 700; color: #111827; line-height: 1.2; }
  .sig-cell .sig-title { font-size: 10px; color: #4b5563; line-height: 1.2; }
  .sig-cell .sig-role { font-size: 9px; color: #6b7280; letter-spacing: 0.06em; margin-top: 2px; text-transform: uppercase; }
  /* Footer band */
  .band-bottom {
    background: #eef2ff; border-top: 1px solid #c7d2fe;
    padding: 8px 16px; display: flex; align-items: center;
    justify-content: space-between; gap: 12px; font-size: 10.5px; color: #1e1b4b;
  }
  .band-bottom .verify { flex: 1; }
  .band-bottom .verify .mono { font-family: 'Courier New', monospace; font-size: 10px; }
  .band-bottom .barcode { font-family: 'Courier New', monospace; letter-spacing: 3px; font-weight: 800; font-size: 12px; padding: 3px 10px; background: #fff; border: 1px solid #c7d2fe; border-radius: 3px; }
  .band-bottom .page-x { font-weight: 700; }
`;

const isMobileUA = () =>
  typeof navigator !== 'undefined' &&
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');

// v1.9.108 — single PDF renderer shared by download + share.
// The receipt is staged off-screen only so its <img> tags can load; html2pdf
// is handed the INNER element (no off-screen styles). html2pdf deep-clones
// whatever it's given into its own hidden overlay, so passing the off-screen
// wrapper used to clone `left:-99999px` too → html2canvas captured an empty
// area → blank PDF.
const renderReceiptPdf = async (cr, profile = {}) => {
  const wrap = document.createElement('div');
  wrap.style.position = 'fixed';
  wrap.style.left = '-99999px';
  wrap.style.top = '0';
  wrap.style.width = '210mm';
  wrap.style.pointerEvents = 'none';
  wrap.innerHTML = `<style>${RECEIPT_STYLES}</style>
    <div data-receipt-root style="width:190mm;background:#fff">${buildReceiptBlock(cr, profile, 'ORIGINAL')}</div>`;
  document.body.appendChild(wrap);
  try {
    await waitForImages(wrap);
    const filename = `Receipt-${cr.cr_number || 'CR'}.pdf`;
    // Generate ONCE — an html2pdf worker is consumed by its first output call.
    const dataUri = await html2pdf().from(wrap.querySelector('[data-receipt-root]')).set({
      margin: 8,
      filename,
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true, scrollX: 0, scrollY: 0 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    }).outputPdf('datauristring');
    const b64 = (dataUri || '').includes(',') ? dataUri.split(',')[1] : '';
    if (!b64 || b64.length < 100) throw new Error('PDF generation returned empty output');
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'application/pdf' });
    return { b64, blob, filename };
  } finally {
    try { document.body.removeChild(wrap); } catch {}
  }
};

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// v1.9.94 — Android WebView ignores blob: URLs and <a download>, but hands a
// data:application/pdf URL to the native PDF viewer. Returns true if opened.
const openPdfDataUrl = (b64) => {
  const dataUrl = `data:application/pdf;base64,${b64}`;
  if (window.open(dataUrl, '_blank')) return true;
  try { window.location.href = dataUrl; return true; } catch { return false; }
};

export const downloadCashReceipt = async (cr, profile = {}) => {
  const { b64, blob, filename } = await renderReceiptPdf(cr, profile);
  if (isMobileUA()) {
    const file = new File([blob], filename, { type: 'application/pdf' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: `Receipt ${cr.cr_number || ''}` });
        return;
      } catch (_) { /* cancelled or tap expired — fall through to open */ }
    }
    openPdfDataUrl(b64);
  } else {
    saveBlob(blob, filename);
  }
};

// v1.9.108 — prepared-PDF cache. Browsers only open the share sheet within a
// few seconds of a tap ("user activation"); building the PDF on a phone takes
// longer than that, so navigator.share() was rejected and the user saw only
// "Preparing…". Screens call prepareCashReceiptShare() ahead of time (or the
// first tap prepares it), so the share tap itself is instant.
const preparedCache = new Map();
const cacheKey = (cr, profile) => JSON.stringify([cr, profile]);

const getPrepared = (cr, profile) => {
  const key = cacheKey(cr, profile);
  let entry = preparedCache.get(key);
  if (!entry) {
    entry = { ready: false, promise: null };
    entry.promise = renderReceiptPdf(cr, profile).then(
      (res) => { entry.ready = true; return res; },
      (err) => { preparedCache.delete(key); throw err; }, // let a later tap retry
    );
    preparedCache.set(key, entry);
    if (preparedCache.size > 10) preparedCache.delete(preparedCache.keys().next().value);
  }
  return entry;
};

// Build the PDF in the background so a later Share tap is instant.
export const prepareCashReceiptShare = (cr, profile = {}) => {
  getPrepared(cr, profile).promise.catch(() => {});
};

// Share a Cash Receipt PDF via the OS share sheet (WhatsApp / Telegram /
// Email …). Returns { shared }:
//   'native-capacitor' | 'native-with-file' — share sheet opened
//   'cancelled'  — user closed the sheet
//   'needs-tap'  — PDF is ready but the browser blocked the sheet because the
//                  tap expired while generating; tapping Share again works
//   'opened'     — no share support; PDF opened in the viewer / new tab
//   'downloaded' — couldn't open it either; PDF saved instead
export const shareCashReceipt = async (cr, profile = {}) => {
  const entry = getPrepared(cr, profile);
  const wasReady = entry.ready;
  const { b64, blob, filename } = await entry.promise;

  const verifyUrl = cr.verify_token ? `${window.location.origin}/verify/${cr.verify_token}` : '';
  const caption = `${profile.church_name || 'Church'}\nReceipt ${cr.cr_number || ''}${verifyUrl ? `\nVerify: ${verifyUrl}` : ''}`;
  const title = `Receipt ${cr.cr_number || ''}`;

  // === Capacitor APK path — native plugin, no tap-timing limit =============
  // v1.9.111 — browser fallbacks (navigator.share, data:/blob: tabs, <a download>)
  // all do nothing inside the Android WebView, so in the app we never fall
  // through to them: either the native plugins work or we say why not.
  if (isCapacitor()) {
    const { Capacitor } = require('@capacitor/core');
    if (!Capacitor.isPluginAvailable('Share') || !Capacitor.isPluginAvailable('Filesystem')) {
      throw new Error('Sharing is not available in this version of the app. Please install the latest app update.');
    }
    try {
      const { Filesystem, Directory } = require('@capacitor/filesystem');
      const { Share } = require('@capacitor/share');
      const written = await Filesystem.writeFile({ path: filename, data: b64, directory: Directory.Cache, recursive: true });
      await Share.share({ title, text: caption, url: written.uri, dialogTitle: 'Share receipt' });
      return { shared: 'native-capacitor' };
    } catch (err) {
      if (err && (/cancel/i.test(err.message || '') || err.name === 'AbortError')) return { shared: 'cancelled' };
      console.warn('[share] Capacitor native share failed:', err);
      throw new Error(`Could not share the receipt (${err?.message || 'error'})`);
    }
  }

  // === Web browser path (Chrome/Safari mobile) ==============================
  const file = new File([blob], filename, { type: 'application/pdf' });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title, text: caption });
      return { shared: 'native-with-file' };
    } catch (err) {
      if (err && err.name === 'AbortError') return { shared: 'cancelled' };
      // Tap expired while the PDF was being built — it's cached now, so a
      // second tap shares instantly.
      if (!wasReady && err && err.name === 'NotAllowedError') return { shared: 'needs-tap' };
      throw new Error(`Could not open the share menu (${err?.name || 'error'}). Use Download PDF instead.`);
    }
  }

  // === No share support: hand the PDF to the OS ==============================
  // data: URL first (only form Android WebView opens), blob tab, then save.
  if (window.open(`data:application/pdf;base64,${b64}`, '_blank')) return { shared: 'opened', filename };
  const url = URL.createObjectURL(blob);
  if (window.open(url, '_blank')) {
    setTimeout(() => URL.revokeObjectURL(url), 30000);
    return { shared: 'opened', filename };
  }
  URL.revokeObjectURL(url);
  saveBlob(blob, filename);
  return { shared: 'downloaded', filename };
};

// v1.9.18 — wait for every <img> in the popup to actually finish loading
// before triggering print. Previously the fixed 500 ms delay wasn't enough
// for the 1.4 MB /logo.png on slower networks — Chrome fired print with a
// broken-image circle instead of the church seal.
const waitForImages = (root) => new Promise((resolve) => {
  // v1.9.46 — accept EITHER a document OR a plain Element. The previous
  // `root.images` shortcut only exists on documents (HTMLCollection),
  // so calling this with a div silently returned zero images and html2pdf
  // ran before any of the receipt's <img> tags actually loaded → blank PDF.
  const imgs = Array.from(
    (root && typeof root.getElementsByTagName === 'function')
      ? root.getElementsByTagName('img')
      : (root && root.images) || []
  );
  if (!imgs.length) return resolve();
  let done = 0;
  const check = () => { if (++done >= imgs.length) resolve(); };
  imgs.forEach(img => {
    if (img.complete && img.naturalWidth > 0) check();
    else { img.addEventListener('load', check, { once: true }); img.addEventListener('error', check, { once: true }); }
  });
  // Hard cap: never wait more than 4 s even if a request stalls
  setTimeout(resolve, 4000);
});

// v1.9.117 — printed CR = two copies on one A4 (ORIGINAL on top, COPY below)
// split by a dashed cut line. Each copy gets half the printable height
// (297mm - 2×8mm margin = 281mm → 135 + 9 + 135, 2mm spare). The body is
// pinned to the printable width (210 - 2×8mm) so on-screen layout matches
// print, and fitDuplicateHalves() shrinks a copy only when it is taller than
// its half (several fund rows, long notes).
const DUPLICATE_STYLES = `
  body { width: 194mm; }
  .dup-half { height: 135mm; overflow: hidden; }
  .dup-cut { height: 9mm; position: relative; }
  .dup-cut::before { content: ''; position: absolute; left: 0; right: 0; top: 50%; border-top: 1.5px dashed #6b7280; }
  .dup-cut span { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); padding: 0 8px; background: #fff; color: #6b7280; font-size: 9px; letter-spacing: 0.08em; }
`;

const fitDuplicateHalves = (doc) => {
  doc.querySelectorAll('.dup-half').forEach((half) => {
    const page = half.firstElementChild;
    if (!page) return;
    page.style.zoom = '1';
    const scale = half.clientHeight / page.scrollHeight;
    if (scale < 1) page.style.zoom = String(Math.floor(scale * 100) / 100);
  });
};

export const printCashReceipt = (cr, profile = {}) => {
  const w = window.open('', '_blank', 'width=900,height=1200');
  if (!w) { alert('Please allow pop-ups to print the receipt'); return; }
  w.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>Receipt ${esc(cr.cr_number || '')}</title>
  <style>${RECEIPT_STYLES}${DUPLICATE_STYLES}</style>
</head>
<body>
  <div class="dup-half">${buildReceiptBlock(cr, profile, 'ORIGINAL')}</div>
  <div class="dup-cut"><span>✂ cut here</span></div>
  <div class="dup-half">${buildReceiptBlock(cr, profile, 'COPY')}</div>
</body>
</html>`);
  w.document.close();
  waitForImages(w.document).then(() => { try { fitDuplicateHalves(w.document); w.focus(); w.print(); } catch {} });
};

// ─── Payment Voucher (single) ──────────────────────────────────────────────
const buildVoucherBlock = (pv, profile, copyLabel) => {
  const contactBits = [profile.church_address, profile.church_phone, profile.church_email].filter(Boolean).map(esc);
  const currencies = (profile.currencies && profile.currencies.length) ? profile.currencies : [profile.currency_primary || '$'];
  const cur1 = pv.currency || currencies[0] || '$';
  const amountFmt = formatAmount(pv.amount, cur1);
  return `
    <section class="a4-page">
      <header class="band-top">
        <div class="logo"><img src="${esc(LOGO_URL())}" alt="" /></div>
        <div class="titles">
          <h1>${esc(profile.church_name || 'My Church')}</h1>
          ${profile.hagest_sbket || profile.city ? `<div class="subtitle">${esc([profile.hagest_sbket, profile.city].filter(Boolean).join(' · '))}</div>` : ''}
          ${contactBits.length ? `<div class="contact">${contactBits.join(' · ')}</div>` : ''}
        </div>
        <div class="qr"><div class="qr-placeholder"></div><div class="qr-lbl">PV</div></div>
      </header>
      <span class="copy-badge">${esc(copyLabel)}</span>
      <div class="body">
        <h2 class="rct-title">Payment Voucher</h2>
        <div class="fields">
          <div class="field"><span class="n">1.</span><span class="k">PV Number:</span><span class="v acc">${esc(pv.pv_number || '—')}</span></div>
          <div class="field"><span class="n">2.</span><span class="k">Date:</span><span class="v strong">${esc(pv.date || '—')}</span></div>
          <div class="field"><span class="n">3.</span><span class="k">Paid To:</span><span class="v strong">${esc(pv.paid_to || '—')}</span></div>
          <div class="field"><span class="n">4.</span><span class="k">Reason / Type:</span><span class="v">${esc(pv.expense_category_name || '—')}</span></div>
          <div class="field"><span class="n">5.</span><span class="k">Description:</span><span class="v">${esc(pv.description || '—')}</span></div>
          <div class="field"><span class="n">6.</span><span class="k">Status:</span><span class="v cap">${esc((pv.status || '—').replace('_', ' '))}</span></div>
        </div>
        <div class="items">
          <table>
            <thead><tr><th class="idx">#</th><th>Description</th><th class="num">Amount</th><th class="ccy">Currency</th></tr></thead>
            <tbody>
              <tr><td>1</td><td>${esc(pv.description || pv.expense_category_name || '—')}</td><td class="num">${esc(amountFmt.replace(cur1, ''))}</td><td class="num">${esc(cur1)}</td></tr>
              <tr class="total"><td colspan="2" style="text-align:right">TOTAL</td><td class="num">${esc(amountFmt.replace(cur1, ''))}</td><td class="num">${esc(cur1)}</td></tr>
            </tbody>
          </table>
        </div>
        ${buildSignoff(profile, { receivedLabel: 'PAID BY' })}
      </div>
      <footer class="band-bottom">
        <span class="verify"></span>
        <span class="barcode">*${esc((pv.pv_number || '').replace(/[^A-Z0-9]/gi, ''))}*</span>
        <span class="page-x">1 of 1</span>
      </footer>
    </section>
  `;
};

// v1.9.121 — Payment Requisition Form, printed on the lower half of the
// voucher page. "Requested by" is the user who initiated the payment (the PV
// creator). Signature lines are left blank to be signed by hand — the
// requisition is signed before the money is paid out.
const buildRequisitionBlock = (pv, profile) => {
  const p = profile || {};
  const currencies = (p.currencies && p.currencies.length) ? p.currencies : [p.currency_primary || '$'];
  const cur1 = pv.currency || currencies[0] || '$';
  const amountFmt = formatAmount(pv.amount, cur1);
  const sigCell = (name, title, role) => `
      <div class="sig-cell">
        <div class="sig-img"></div>
        <div class="sig-line"></div>
        <div class="sig-name">${esc(name || '—')}</div>
        <div class="sig-title">${esc(title || '')}</div>
        <div class="sig-role">${esc(role)}</div>
      </div>`;
  return `
    <section class="a4-page">
      <header class="band-top">
        <div class="logo"><img src="${esc(LOGO_URL())}" alt="" /></div>
        <div class="titles">
          <h1>${esc(p.church_name || 'My Church')}</h1>
          ${p.hagest_sbket || p.city ? `<div class="subtitle">${esc([p.hagest_sbket, p.city].filter(Boolean).join(' · '))}</div>` : ''}
        </div>
        <div class="qr"><div class="qr-placeholder"></div><div class="qr-lbl">REQ</div></div>
      </header>
      <div class="body">
        <h2 class="rct-title">Payment Requisition Form</h2>
        <div class="fields">
          <div class="field"><span class="n">1.</span><span class="k">For PV Number:</span><span class="v acc">${esc(pv.pv_number || '—')}</span></div>
          <div class="field"><span class="n">2.</span><span class="k">Date:</span><span class="v strong">${esc(pv.date || '—')}</span></div>
          <div class="field"><span class="n">3.</span><span class="k">Requested By:</span><span class="v strong">${esc(pv.created_by_name || '—')}</span></div>
          <div class="field"><span class="n">4.</span><span class="k">Pay To:</span><span class="v strong">${esc(pv.paid_to || '—')}</span></div>
          <div class="field"><span class="n">5.</span><span class="k">Reason / Type:</span><span class="v">${esc(pv.expense_category_name || '—')}</span></div>
          <div class="field"><span class="n">6.</span><span class="k">Amount Requested:</span><span class="v acc">${esc(amountFmt)}</span></div>
        </div>
        <div class="req-purpose"><span class="lbl">Purpose / Description:</span> ${esc(pv.description || '—')}</div>
        <footer class="signoff-3col">
          <div class="sig-row">
            ${sigCell(pv.created_by_name, '', 'REQUESTED BY')}
            ${sigCell(p.signatory_received_name, p.signatory_received_title, 'CHECKED BY')}
            ${sigCell(p.signatory_approved_name, p.signatory_approved_title, 'APPROVED BY')}
          </div>
        </footer>
      </div>
    </section>
  `;
};

const REQUISITION_STYLES = `
  .req-purpose { margin-top: 10px; padding: 8px 10px; min-height: 44px; border: 1px dashed #c7d2fe; border-radius: 4px; font-size: 11.5px; color: #111827; }
  .req-purpose .lbl { font-weight: 700; color: #374151; }
`;

export const printPaymentVoucher = (pv, profile = {}) => {
  const w = window.open('', '_blank', 'width=900,height=1200');
  if (!w) { alert('Please allow pop-ups to print the voucher'); return; }
  w.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>Voucher ${esc(pv.pv_number || '')}</title>
  <style>${RECEIPT_STYLES}${DUPLICATE_STYLES}${REQUISITION_STYLES}</style>
</head>
<body>
  <div class="dup-half">${buildVoucherBlock(pv, profile, 'ORIGINAL')}</div>
  <div class="dup-cut"><span>✂ cut here</span></div>
  <div class="dup-half">${buildRequisitionBlock(pv, profile)}</div>
</body>
</html>`);
  w.document.close();
  waitForImages(w.document).then(() => { try { fitDuplicateHalves(w.document); w.focus(); w.print(); } catch {} });
};

// ─── List prints (CR list, PV list) ─────────────────────────────────────────
const LIST_STYLES = `
  @page { size: A4; margin: 10mm; }
  * { box-sizing: border-box; }
  body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans Ethiopic', Arial, sans-serif; color: #111827; }
  .head { text-align: center; border-bottom: 2px solid #1e1b4b; padding-bottom: 8px; margin-bottom: 12px; }
  .head h1 { font-size: 20px; margin: 0; color: #1e1b4b; }
  .head .contact { font-size: 10px; color: #6b7280; margin-top: 3px; }
  .title-bar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
  .title-bar h2 { font-size: 15px; margin: 0; letter-spacing: 0.08em; }
  .meta { font-size: 11px; color: #6b7280; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 10px; }
  th { background: #f3f4f6; text-align: left; padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 10.5px; }
  td { padding: 6px 8px; border-bottom: 1px solid #f3f4f6; }
  tr:nth-child(even) td { background: #fafafa; }
  tfoot td { font-weight: 700; background: #f3f4f6; border-top: 2px solid #d1d5db; }
  .right { text-align: right; }
  .footer { margin-top: 14px; font-size: 9.5px; color: #9ca3af; display: flex; justify-content: space-between; }
`;

export const printCRList = ({ records, profile = {}, from = '', to = '', filterLabel = '', preparedBy = '', financeName = '', cashierName = '' }) => {
  const w = window.open('', '_blank', 'width=900,height=1000');
  if (!w) { alert('Please allow pop-ups to print the list'); return; }
  const headerLines = [profile.church_address, profile.church_phone, profile.church_email].filter(Boolean).map(esc).join(' · ');
  const currencies = (profile.currencies && profile.currencies.length) ? profile.currencies : [profile.currency_primary || '$', profile.currency_secondary || 'K'];
  const cur1 = currencies[0] || '$';
  const cur2 = currencies[1] || 'K';
  // Per-currency totals over the visible CR list. Falls back to legacy columns
  // for old rows where amounts_json wasn't populated.
  const totalsByCurrency = currencies.reduce((acc, sym) => {
    acc[sym] = records.reduce((s, r) => {
      const amts = r.amounts && typeof r.amounts === 'object'
        ? r.amounts
        : { [cur1]: r.amount_usd, [cur2]: r.amount_kwacha };
      return s + (parseFloat(amts[sym]) || 0);
    }, 0);
    return acc;
  }, {});
  const printedAt = new Date().toLocaleString();
  const rows = records.map((r, i) => `
    <tr>
      <td>${i + 1}</td>
      <td><strong>${esc(r.cr_number || '')}</strong></td>
      <td>${esc(r.date || '')}</td>
      <td>${esc(r.member_name?.trim() || r.received_from || '—')}</td>
      <td>${esc(r.spiritual_father_name || '—')}</td>
      <td>${esc(r.fund_name || '—')}</td>
      <td class="cap">${esc((r.payment_method || '—').replace('_',' '))}</td>
      ${(() => {
        const amts = r.amounts && typeof r.amounts === 'object'
          ? r.amounts
          : { [cur1]: r.amount_usd, [cur2]: r.amount_kwacha };
        return currencies.map(sym => {
          const v = parseFloat(amts[sym] || 0);
          return `<td class="right">${v > 0 ? `${esc(sym)}${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}</td>`;
        }).join('');
      })()}
    </tr>
  `).join('');
  const dateRange = (from || to) ? `${esc(from || 'all')} → ${esc(to || 'all')}` : 'All dates';
  const sigBlock = `
    <div class="sig-block">
      <div class="sig-col">
        <div class="sig-label">Prepared by</div>
        <div class="sig-name">${esc(preparedBy || '—')}</div>
        <div class="sig-line"></div>
        <div class="sig-foot">Signature</div>
      </div>
      <div class="sig-col">
        <div class="sig-label">Handed over (Finance)</div>
        <div class="sig-name">${esc(financeName || '—')}</div>
        <div class="sig-line"></div>
        <div class="sig-foot">Signature</div>
      </div>
      <div class="sig-col">
        <div class="sig-label">Received by (Cashier)</div>
        <div class="sig-name">${esc(cashierName || '—')}</div>
        <div class="sig-line"></div>
        <div class="sig-foot">Signature</div>
      </div>
    </div>
  `;
  const sigStyles = `
    .sig-block { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; margin-top: 28px; padding-top: 16px; border-top: 1px solid #e5e7eb; page-break-inside: avoid; }
    .sig-col { text-align: center; }
    .sig-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: #6b7280; font-weight: 700; }
    .sig-name { font-size: 12px; color: #111827; font-weight: 600; margin: 16px 0 6px; min-height: 16px; }
    .sig-line { border-bottom: 1px solid #111827; height: 1px; margin: 0 8px; }
    .sig-foot { font-size: 9.5px; color: #9ca3af; margin-top: 4px; }
  `;
  w.document.write(`<!doctype html>
<html><head><meta charset="utf-8"><title>Cash Receipts</title><style>${LIST_STYLES}${sigStyles}</style></head>
<body>
  <header class="head">
    <h1>${esc(profile.church_name || 'My Church')}</h1>
    ${headerLines ? `<div class="contact">${headerLines}</div>` : ''}
  </header>
  <div class="title-bar">
    <h2>CASH RECEIPTS LIST</h2>
    <div class="meta">${dateRange}${filterLabel ? ' · ' + esc(filterLabel) : ''}</div>
  </div>
  <table>
    <thead><tr><th>#</th><th>CR No.</th><th>Date</th><th>Received From</th><th>Spiritual Father</th><th>Fund</th><th>Method</th>${currencies.map(sym => `<th class="right">Amount ${esc(sym)}</th>`).join('')}</tr></thead>
    <tbody>${rows || `<tr><td colspan="9" style="text-align:center;padding:20px;color:#9ca3af">No records</td></tr>`}</tbody>
    ${records.length ? `<tfoot><tr><td colspan="7">TOTAL — ${records.length} receipt${records.length === 1 ? '' : 's'}</td>${currencies.map(sym => `<td class="right">${esc(sym)}${(totalsByCurrency[sym] || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>`).join('')}</tr></tfoot>` : ''}
  </table>
  ${sigBlock}
  <div class="footer"><span>${esc(profile.church_name || '')}</span><span>Printed: ${esc(printedAt)}</span></div>
</body></html>`);
  w.document.close();
  waitForImages(w.document).then(() => { try { w.focus(); w.print(); } catch {} });
};

export const printPVList = ({ records, profile = {}, from = '', to = '', filterLabel = '' }) => {
  const w = window.open('', '_blank', 'width=900,height=1000');
  if (!w) { alert('Please allow pop-ups to print the list'); return; }
  const headerLines = [profile.church_address, profile.church_phone, profile.church_email].filter(Boolean).map(esc).join(' · ');
  const defaultCur = (profile.currencies && profile.currencies[0]) || profile.currency_primary || '$';
  const total = records.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const printedAt = new Date().toLocaleString();
  const rows = records.map((r, i) => `
    <tr>
      <td>${i + 1}</td>
      <td><strong>${esc(r.pv_number || '')}</strong></td>
      <td>${esc(r.date || '')}</td>
      <td>${esc(r.paid_to || '—')}</td>
      <td>${esc(r.description || '—')}</td>
      <td class="cap">${esc((r.status || '—').replace('_',' '))}</td>
      <td class="right"><strong>${esc(r.currency || defaultCur)}${parseFloat(r.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></td>
    </tr>
  `).join('');
  const dateRange = (from || to) ? `${esc(from || 'all')} → ${esc(to || 'all')}` : 'All dates';
  w.document.write(`<!doctype html>
<html><head><meta charset="utf-8"><title>Payment Vouchers</title><style>${LIST_STYLES}</style></head>
<body>
  <header class="head">
    <h1>${esc(profile.church_name || 'My Church')}</h1>
    ${headerLines ? `<div class="contact">${headerLines}</div>` : ''}
  </header>
  <div class="title-bar">
    <h2>PAYMENT VOUCHERS LIST</h2>
    <div class="meta">${dateRange}${filterLabel ? ' · ' + esc(filterLabel) : ''}</div>
  </div>
  <table>
    <thead><tr><th>#</th><th>PV No.</th><th>Date</th><th>Paid To</th><th>Description</th><th>Status</th><th class="right">Amount ${esc(defaultCur)}</th></tr></thead>
    <tbody>${rows || `<tr><td colspan="7" style="text-align:center;padding:20px;color:#9ca3af">No records</td></tr>`}</tbody>
    ${records.length ? `<tfoot><tr><td colspan="6">TOTAL — ${records.length} voucher${records.length === 1 ? '' : 's'}</td><td class="right">${esc(defaultCur)}${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr></tfoot>` : ''}
  </table>
  <div class="footer"><span>${esc(profile.church_name || '')}</span><span>Printed: ${esc(printedAt)}</span></div>
</body></html>`);
  w.document.close();
  waitForImages(w.document).then(() => { try { w.focus(); w.print(); } catch {} });
};
